/**
 * IframeAgent Mode: Runs inside the customer's product iframe during
 * autonomous discovery. Listens for postMessage commands from the parent
 * autonomous discovery page and executes them.
 *
 * Handles all standard automation commands (click, type, navigate, etc.)
 * plus the `da:capture-snapshot` command for rich DOM capture.
 */

import {
  capturePageSnapshot,
  extractScreenObservation,
  extractScreenObservationAsync,
  type PageSnapshot,
  type ScreenObservation,
} from '../capture/element-mapper.js';
import { sanitizeSnapshot } from '../capture/sanitizer.js';
import { Recorder } from '../capture/recorder.js';
import {
  clickElement,
  typeText,
  selectOption,
  scrollTo,
  highlightElement,
  waitForElement,
  pressKey,
  uploadFile,
  drag,
  resolveElement,
  showRecoveryToast,
  type VerificationResult,
  type UploadFileSpec,
} from '../automation/actions.js';
import {
  evaluatePostCondition,
  type PostCondition,
  type PostConditionState,
} from '../automation/post-condition.js';
import { animateCursorTo, showClickRipple, hideCursor } from '../automation/cursor.js';
import { tagInteractiveElements } from '../automation/stable-handles.js';
import {
  dismissCookieBanner,
  dismissOnboardingTour,
  drainLiveRegions,
} from '../automation/popup-handlers.js';
import {
  installNetworkShim,
  drainNetworkEventsSince,
  drainAmbientSignals,
  computeNetworkStatus,
  getLastNetworkEventId,
  waitForNetworkIdle,
} from '../capture/network-events.js';

// Credentials handed down from the parent so the iframe can POST rrweb
// batches to /api/v1/session/:id/record without putting the JWT in its URL.
interface IraaSessionMessage {
  __iraa: 'session';
  apiUrl: string;
  sessionId: string;
  sessionToken: string;
}

function isIraaSessionMessage(data: unknown): data is IraaSessionMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { __iraa?: unknown }).__iraa === 'session' &&
    typeof (data as IraaSessionMessage).apiUrl === 'string' &&
    typeof (data as IraaSessionMessage).sessionId === 'string' &&
    typeof (data as IraaSessionMessage).sessionToken === 'string'
  );
}

// ─── Types ────────────────────────────────────────────────────────────────

interface DAMessage {
  __da: true;
  id: string;
  gen: number;
  ts: number;
  dir: 'parent-to-iframe' | 'iframe-to-parent';
  type: string;
  payload: Record<string, unknown>;
}

export interface IframeAgentConfig {
  sessionId: string;
  token: string;
}

/**
 * Shape that state-changing commands return so the parent can read three
 * additive signals without a breaking change:
 *  - `recoveredSelector` (legacy)        — selector-drift recovery (#42)
 *  - `observation`        (PR earlier)   — post-action screen state
 *  - `verification`       (PR1, this PR) — did the action actually take effect
 *
 * Returns null when nothing is worth reporting — preserves the pre-existing
 * "no result" wire shape for boring actions.
 */
/**
 * Read `expects` from a command payload, validate the kind tag, and return
 * the typed PostCondition. Server side already lints these before send;
 * this is a defensive runtime check so a malformed payload doesn't crash
 * the iframe loop.
 */
function readExpects(payload: Record<string, unknown>): PostCondition | undefined {
  const e = payload.expects;
  if (!e || typeof e !== 'object') return undefined;
  const kind = (e as { kind?: unknown }).kind;
  if (typeof kind !== 'string') return undefined;
  if (![
    'modal_open',
    'modal_closed',
    'aria_expanded',
    'aria_pressed',
    'value_set',
    'route_matches',
    'element_visible',
    'text_appears',
    'text_disappears',
  ].includes(kind)) return undefined;
  return e as PostCondition;
}

/**
 * Evaluate the action's declared post-condition AFTER the verifier's settle
 * window. Mutates the passed `verification` to include the typed result.
 *
 * `targetSelector` is the selector the action ran against — the evaluator
 * needs it for aria_*, value_set, and element_visible (which look at the
 * subject element directly). For route/modal conditions the subject is
 * irrelevant.
 */
function applyPostCondition(
  verification: VerificationResult | undefined,
  expects: PostCondition | undefined,
  targetSelector: string | undefined,
  fallback: Record<string, unknown> | undefined,
): VerificationResult | undefined {
  if (!verification || !expects) return verification;
  let subject: Element | null = null;
  if (targetSelector) {
    try {
      const r = resolveElement(targetSelector, fallback as never);
      subject = r ? r.element : null;
    } catch { /* ignore — evaluator handles null subject */ }
  }
  const state: PostConditionState = evaluatePostCondition(expects, subject);
  verification.postCondition = { state, cond: expects };
  return verification;
}

function packActionResult(
  recoveredSelector: string | undefined,
  observation: ScreenObservation | null,
  verification?: VerificationResult,
): {
  recoveredSelector?: string;
  observation?: ScreenObservation;
  verification?: VerificationResult;
} | null {
  // P1 (#90): visible recovery cue. When verification came back failed
  // or executed-without-effect, briefly show a toast so the visitor sees
  // that the agent noticed — pairs with the spoken acknowledgement.
  if (verification?.status === 'failed') {
    try {
      const code = verification.errors?.[0]?.code ?? 'verification failed';
      showRecoveryToast(`Hmm — that didn't take. Trying a different path.`);
      void code;
    } catch { /* ignore */ }
  }
  if (!recoveredSelector && !observation && !verification) return null;
  const out: {
    recoveredSelector?: string;
    observation?: ScreenObservation;
    verification?: VerificationResult;
  } = {};
  if (recoveredSelector) out.recoveredSelector = recoveredSelector;
  if (observation) out.observation = observation;
  if (verification) out.verification = verification;
  return out;
}

// ─── Main Class ───────────────────────────────────────────────────────────

export class IframeAgentMode {
  private config: IframeAgentConfig;
  private currentGen = 0;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private navPollInterval: ReturnType<typeof setInterval> | null = null;
  private lastUrl = '';
  private idCounter = 0;
  private cleanupFns: Array<() => void> = [];
  private recorder: Recorder | null = null;
  /**
   * D32 — command idempotency dedup. Maps a hashed (type+selector+payload)
   * key to the timestamp of the most recent execution. New commands whose
   * key fired within DEDUP_WINDOW_MS short-circuit with the cached result
   * instead of re-executing. Stops accidental double-clicks (network
   * retry, agent re-emit) from toggling state back off, and stops
   * double-typing into a field that already has the value.
   *
   * Capture-only commands (da:capture-* / da:ping / da:wait) are
   * exempt — they're side-effect-free reads, dedup buys nothing.
   */
  private recentCommands: Map<string, { ts: number; result: unknown }> = new Map();
  private static readonly DEDUP_WINDOW_MS = 250;

  constructor(config: IframeAgentConfig) {
    this.config = config;
  }

  start(): void {
    this.lastUrl = location.href;
    window.addEventListener('message', this.handleMessage);
    window.addEventListener('message', this.handleSessionCredentials);
    this.startHeartbeat();
    this.observeNavigation();
    this.interceptPopups();
    this.interceptNativeDialogs();
    this.interceptDownloads();
    // Phase 1: patch fetch/XHR before any customer code runs requests we
    // need to see (auth checks, form submits). Idempotent. Failures are
    // surfaced as a `shim-disabled` ambient signal — non-fatal.
    try { installNetworkShim(); } catch { /* never crash the iframe */ }

    // Cluster C item 27 — tag every interactive element with a stable
    // data-iraa-id handle. Survives most class renames + wrapper-div
    // restructures, so tools that address by handle keep working when
    // selectors drift. Re-runs on mutation to catch dynamically-added
    // elements (route changes, modal opens).
    try {
      tagInteractiveElements(document);
      const reTagObserver = new MutationObserver(() => {
        tagInteractiveElements(document);
      });
      if (document.body) {
        reTagObserver.observe(document.body, { childList: true, subtree: true });
      }
    } catch { /* non-fatal — handles are an addressing aid, not load-bearing */ }

    // SPA load barrier (PR2). Don't fire `da:ready` until the page is
    // genuinely ready — readyState=complete AND a 300ms quiet period of no
    // DOM mutations. Without this the parent thinks the page is interactive
    // when really React hasn't hydrated, the agent fires its first click,
    // and the click vanishes into a static stub of the route. Capped at 5s
    // so a busy page that never quiesces (animations, polling) still boots.
    this.signalReadyWhenLoaded();
    console.log('[DemoAgent] IframeAgent mode - boot');
  }

  private signalReadyWhenLoaded(): void {
    const url = location.href;
    const fire = (): void => this.sendToParent('da:ready', { url, loaded: true });

    const start = Date.now();
    const MAX = 5000;
    const QUIET = 300;

    const waitForReadyState = async (): Promise<void> => {
      if (document.readyState === 'complete') return;
      await new Promise<void>((resolve) => {
        const onReady = (): void => {
          if (document.readyState === 'complete') {
            document.removeEventListener('readystatechange', onReady);
            resolve();
          }
        };
        document.addEventListener('readystatechange', onReady);
      });
    };

    const waitForQuiet = async (budgetMs: number): Promise<void> => {
      let lastMutation = Date.now();
      const observer = new MutationObserver(() => { lastMutation = Date.now(); });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      try {
        while (Date.now() - start < MAX) {
          if (Date.now() - lastMutation >= budgetMs) return;
          await new Promise((r) => setTimeout(r, 80));
        }
      } finally {
        observer.disconnect();
      }
    };

    // Fire-and-forget; never block the event loop on a stalled page.
    void (async () => {
      try {
        await waitForReadyState();
        await waitForQuiet(QUIET);
        // Cluster F items 48, 49 — auto-dismiss cookie banners and product
        // tours BEFORE we fire `da:ready`. The agent's first action almost
        // always misses if a modal is in front of the UI; clearing them here
        // keeps the demo on its rails. Best-effort; never throw out.
        try {
          dismissCookieBanner();
        } catch { /* non-fatal */ }
        try {
          dismissOnboardingTour();
        } catch { /* non-fatal */ }
      } catch {
        // Best-effort. If anything throws we still need to signal so the
        // parent isn't waiting forever.
      } finally {
        fire();
      }
    })();
  }

  stop(): void {
    window.removeEventListener('message', this.handleMessage);
    window.removeEventListener('message', this.handleSessionCredentials);
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.navPollInterval) {
      clearInterval(this.navPollInterval);
      this.navPollInterval = null;
    }
    if (this.recorder) {
      try { this.recorder.shutdown(); } catch { /* ignore */ }
      this.recorder = null;
    }
    this.cleanupHighlights();
  }

  /**
   * Listens for the parent's one-shot credentials handoff. Once we have a
   * sessionId + JWT we can start streaming rrweb batches up to the platform.
   * The same message arrives if the iframe navigates inside the customer's
   * site — we ignore subsequent messages once a recorder is already running.
   */
  private handleSessionCredentials = (event: MessageEvent): void => {
    // Recorder credentials must come from the embedding parent only;
    // sibling/popup windows on the same domain cannot inject a different
    // session token.
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!isIraaSessionMessage(data)) return;
    if (this.recorder) return;
    try {
      this.recorder = new Recorder({
        apiUrl: data.apiUrl,
        sessionId: data.sessionId,
        sessionToken: data.sessionToken,
      });
      this.recorder.start();
    } catch (err) {
      // Recording is best-effort; never block the demo on rrweb startup.
      console.warn('[DemoAgent] Failed to start recorder:', err);
      this.recorder = null;
    }
  };

  // ─── Message Handling ─────────────────────────────────────────────────

  private handleMessage = async (event: MessageEvent): Promise<void> => {
    // Commands must come from the embedding parent only — never let a
    // sibling or popup window inject `da:click` / `da:navigate` etc.
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!this.isDAMessage(data) || data.dir !== 'parent-to-iframe') return;

    // Track generation (drop stale commands)
    if (data.gen < this.currentGen) {
      this.sendResult(data.id, false, 'stale');
      return;
    }
    this.currentGen = data.gen;

    // D32 — idempotency: if the same (type+payload) just executed, return
    // the cached result instead of firing again. Skip for capture-only
    // and idempotent-by-design commands (read-only or self-managed).
    const dedupKey = this.computeDedupKey(data.type, data.payload);
    if (dedupKey) {
      const prior = this.recentCommands.get(dedupKey);
      const now = Date.now();
      if (prior && (now - prior.ts) < IframeAgentMode.DEDUP_WINDOW_MS) {
        this.sendResult(data.id, true, undefined, prior.result);
        return;
      }
    }

    try {
      const result = await this.executeCommand(data.type, data.payload);
      if (dedupKey) {
        this.recentCommands.set(dedupKey, { ts: Date.now(), result });
        // Trim opportunistically — Map iteration is in insertion order so
        // dropping the oldest few when the map grows past 32 keeps memory
        // bounded without per-tick scanning.
        if (this.recentCommands.size > 32) {
          const keys = Array.from(this.recentCommands.keys()).slice(0, 16);
          for (const k of keys) this.recentCommands.delete(k);
        }
      }
      this.sendResult(data.id, true, undefined, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      this.sendResult(data.id, false, message);
    }
  };

  /**
   * D32 — compute a stable dedup key for state-changing commands. Returns
   * null for read-only / self-idempotent commands so they bypass the
   * cache.
   */
  private computeDedupKey(type: string, payload: Record<string, unknown>): string | null {
    if (
      type === 'da:capture-snapshot' ||
      type === 'da:capture-observation' ||
      type === 'da:capture-fingerprint' ||
      type === 'da:capture-dom-debug' ||
      type === 'da:ping' ||
      type === 'da:init' ||
      type === 'da:wait' ||
      type === 'da:wait-for-element' ||
      type === 'da:dismiss-highlights' ||
      type === 'da:inject-token' ||
      type === 'da:highlight'
    ) {
      return null;
    }
    const sel =
      typeof payload.selector === 'string' ? payload.selector :
      typeof payload.url === 'string' ? payload.url :
      typeof payload.fromSelector === 'string' ? `${payload.fromSelector}->${String(payload.toSelector ?? '')}` :
      typeof payload.key === 'string' ? `key:${payload.key}` : '';
    const value =
      typeof payload.text === 'string' ? payload.text :
      typeof payload.value === 'string' ? payload.value : '';
    return `${type}|${sel}|${value}`;
  }

  private isDAMessage(data: unknown): data is DAMessage {
    return (
      typeof data === 'object' &&
      data !== null &&
      '__da' in data &&
      (data as DAMessage).__da === true
    );
  }

  // ─── Command Execution ────────────────────────────────────────────────

  private async executeCommand(
    type: string,
    payload: Record<string, unknown>,
  ): Promise<unknown> {
    switch (type) {
      case 'da:navigate': {
        const url = payload.url as string;
        if (url) window.location.href = url;
        return { url };
      }

      case 'da:click': {
        const selector = payload.selector as string;
        const fallback = payload.fallback as Record<string, unknown> | undefined;
        const intent = typeof payload.intent === 'string' ? payload.intent : undefined;
        const expects = readExpects(payload);
        // The cursor animation is purely cosmetic and is allowed to miss —
        // clickElement does the real lookup with self-healing fallback.
        const animate = payload.cursorAnimate !== false;
        if (animate) {
          try { await animateCursorTo(selector); } catch { /* keep going */ }
          showClickRipple();
        }
        // Intent-first narration (PR3): briefly highlight the target with
        // the agent-supplied intent as the tooltip BEFORE the click fires.
        // Lets the visitor see what's about to happen and read why. The
        // highlight self-dismisses; we keep it short (700ms) so the click
        // still feels responsive.
        if (intent) {
          try {
            const cleanup = highlightElement(selector, intent, 700, fallback as never, 'pulse');
            this.cleanupFns.push(cleanup);
            await new Promise((r) => setTimeout(r, 350));
          } catch { /* highlight is best-effort, keep going to the click */ }
        }
        const r = await clickElement(selector, fallback as never);
        applyPostCondition(r.verification, expects, selector, fallback);
        const observation = await this.captureObservationAfterAction(true);
        return packActionResult(r.recoveredSelector, observation, r.verification);
      }

      case 'da:type': {
        const selector = payload.selector as string;
        const text = payload.text as string;
        const speed = (payload.speed as number) ?? 50;
        const fallback = payload.fallback as Record<string, unknown> | undefined;
        const intent = typeof payload.intent === 'string' ? payload.intent : undefined;
        const expects = readExpects(payload);
        if (intent) {
          try {
            const cleanup = highlightElement(selector, intent, 900, fallback as never, 'border');
            this.cleanupFns.push(cleanup);
            await new Promise((r) => setTimeout(r, 350));
          } catch { /* best-effort */ }
        }
        const r = await typeText(selector, text, speed, fallback as never);
        applyPostCondition(r.verification, expects, selector, fallback);
        const observation = await this.captureObservationAfterAction();
        return packActionResult(r.recoveredSelector, observation, r.verification);
      }

      case 'da:fill-select': {
        const selector = payload.selector as string;
        const value = payload.value as string;
        const fallback = payload.fallback as Record<string, unknown> | undefined;
        const intent = typeof payload.intent === 'string' ? payload.intent : undefined;
        const expects = readExpects(payload);
        if (intent) {
          try {
            const cleanup = highlightElement(selector, intent, 900, fallback as never, 'border');
            this.cleanupFns.push(cleanup);
            await new Promise((r) => setTimeout(r, 350));
          } catch { /* best-effort */ }
        }
        const r = await selectOption(selector, value, fallback as never);
        applyPostCondition(r.verification, expects, selector, fallback);
        const observation = await this.captureObservationAfterAction();
        return packActionResult(r.recoveredSelector, observation, r.verification);
      }

      case 'da:scroll-to': {
        const selector = payload.selector as string;
        const fallback = payload.fallback as Record<string, unknown> | undefined;
        const r = await scrollTo(selector, fallback as never);
        const observation = await this.captureObservationAfterAction();
        return packActionResult(r.recoveredSelector, observation, r.verification);
      }

      case 'da:press-key': {
        const key = payload.key as string;
        const target = typeof payload.target === 'string' ? payload.target : undefined;
        const r = await pressKey(key, target);
        // Enter / Space frequently submit forms or open menus that portal-
        // mount; treat them like clicks for modal detection.
        const mayOpenModal = key === 'Enter' || key === ' ' || key === 'Space';
        const observation = await this.captureObservationAfterAction(mayOpenModal);
        return packActionResult(undefined, observation, r.verification);
      }

      case 'da:upload-file': {
        const selector = payload.selector as string;
        const fallback = payload.fallback as Record<string, unknown> | undefined;
        const intent = typeof payload.intent === 'string' ? payload.intent : undefined;
        const fileSpec = (payload.file ?? payload.fileId) as UploadFileSpec | string;
        if (intent) {
          try {
            const cleanup = highlightElement(selector, intent, 700, fallback as never, 'pulse');
            this.cleanupFns.push(cleanup);
            await new Promise((r) => setTimeout(r, 350));
          } catch { /* best-effort */ }
        }
        const r = await uploadFile(selector, fileSpec, fallback as never);
        const observation = await this.captureObservationAfterAction();
        return packActionResult(r.recoveredSelector, observation, r.verification);
      }

      case 'da:drag': {
        const fromSelector = payload.fromSelector as string;
        const toSelector = payload.toSelector as string;
        const fromFallback = payload.fromFallback as Record<string, unknown> | undefined;
        const toFallback = payload.toFallback as Record<string, unknown> | undefined;
        const r = await drag(fromSelector, toSelector, fromFallback as never, toFallback as never);
        const observation = await this.captureObservationAfterAction();
        return packActionResult(undefined, observation, r.verification);
      }

      case 'da:capture-observation': {
        // Parent-driven explicit capture (used after da:nav-change / da:ready
        // so the LLM sees post-nav screen state). No action precedes it, so
        // skip the settle delay — the page is already where it'll be.
        return { observation: extractScreenObservation() };
      }

      case 'da:highlight': {
        const selector = payload.selector as string;
        const tooltip = payload.tooltip as string | undefined;
        const duration = (payload.duration as number) ?? 3000;
        const fallback = payload.fallback as Record<string, unknown> | undefined;
        // Style added by #29 — older parents send no style, default
        // to 'spotlight' so behaviour is unchanged.
        const style = (payload.style as string | undefined) ?? 'spotlight';
        const cleanup = highlightElement(
          selector,
          tooltip,
          duration,
          fallback as never,
          style as never,
        );
        this.cleanupFns.push(cleanup);
        return null;
      }

      case 'da:dismiss-highlights': {
        this.cleanupHighlights();
        return null;
      }

      case 'da:wait': {
        const ms = Math.min((payload.ms as number) ?? 1000, 10000);
        await new Promise((r) => setTimeout(r, ms));
        return null;
      }

      case 'da:wait-for-element': {
        const selector = payload.selector as string;
        const timeout = (payload.timeout as number) ?? 5000;
        await waitForElement(selector, timeout);
        return null;
      }

      case 'da:capture-snapshot': {
        const snapshot = capturePageSnapshot();
        return sanitizeSnapshot(snapshot);
      }

      case 'da:capture-dom-debug': {
        // Diagnostic: dump raw DOM info to understand what's on the page
        const allAnchors = document.querySelectorAll('a');
        const anchorsWithHref = document.querySelectorAll('a[href]');
        const anchorDetails = Array.from(anchorsWithHref).slice(0, 30).map((a) => ({
          href: a.getAttribute('href')?.slice(0, 80),
          text: (a as HTMLElement).innerText?.trim().slice(0, 40) || '(empty)',
          visible: (a as HTMLElement).offsetParent !== null,
          rect: (() => { const r = (a as HTMLElement).getBoundingClientRect(); return `${Math.round(r.left)},${Math.round(r.top)} ${Math.round(r.width)}x${Math.round(r.height)}`; })(),
        }));

        // Find cursor:pointer elements with short text
        const clickables: Array<{ tag: string; text: string; rect: string }> = [];
        document.querySelectorAll('*').forEach((el) => {
          const style = window.getComputedStyle(el as HTMLElement);
          if (style.cursor !== 'pointer') return;
          const text = (el as HTMLElement).innerText?.trim();
          if (!text || text.length > 50 || text.length < 2 || text.includes('\n')) return;
          const r = (el as HTMLElement).getBoundingClientRect();
          if (r.width < 10 || r.height < 10) return;
          clickables.push({
            tag: el.tagName.toLowerCase(),
            text: text.slice(0, 40),
            rect: `${Math.round(r.left)},${Math.round(r.top)} ${Math.round(r.width)}x${Math.round(r.height)}`,
          });
        });

        return {
          url: location.pathname,
          totalElements: document.querySelectorAll('*').length,
          totalAnchors: allAnchors.length,
          anchorsWithHref: anchorsWithHref.length,
          anchorDetails,
          clickableCount: clickables.length,
          clickables: clickables.slice(0, 40),
          bodyTextPreview: document.body.innerText?.slice(0, 300),
        };
      }

      case 'da:capture-fingerprint': {
        // Lightweight page metadata (~10ms vs ~500ms for full snapshot)
        const headings: string[] = [];
        document.querySelectorAll('h1, h2, h3').forEach((h) => {
          const t = (h as HTMLElement).innerText?.trim();
          if (t && t.length < 100) headings.push(t);
        });

        // Extract ALL internal links on the page (not just in nav containers)
        const links: Array<{ text: string; href: string }> = [];
        const seenHrefs = new Set<string>();
        document.querySelectorAll('a[href]').forEach((a) => {
          const href = a.getAttribute('href');
          const text = (a as HTMLElement).innerText?.trim();
          if (!href || !text || text.length > 60 || seenHrefs.has(href)) return;
          if (href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:')) return;
          // Only include internal links (relative or same-origin)
          if (href.startsWith('http') && !href.startsWith(location.origin)) return;
          seenHrefs.add(href);
          links.push({ text: text.slice(0, 40), href });
        });

        // Capture ALL elements that look like navigation items.
        // Many SPA apps (React/Next.js/Vue) use plain <div>, <li>, or <span> elements
        // with JS click handlers and NO cursor:pointer or semantic roles.
        // Strategy: find elements with short text in the left sidebar area (x < 300px)
        // OR elements with cursor:pointer anywhere on the page.
        const allElements = document.querySelectorAll('*');
        for (let i = 0; i < allElements.length && links.length < 50; i++) {
          const el = allElements[i];
          const tag = el.tagName;
          if (tag === 'A' || tag === 'HTML' || tag === 'BODY' || tag === 'HEAD' ||
              tag === 'SCRIPT' || tag === 'STYLE' || tag === 'SVG' || tag === 'PATH' ||
              tag === 'META' || tag === 'LINK' || tag === 'BR' || tag === 'HR' ||
              tag === 'IMG' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') continue;

          const htmlEl = el as HTMLElement;
          const rect = htmlEl.getBoundingClientRect();
          if (rect.width < 20 || rect.height < 10 || rect.height > 60) continue;

          const style = window.getComputedStyle(htmlEl);
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue;

          const isClickable = style.cursor === 'pointer';
          const isButton = tag === 'BUTTON' || el.getAttribute('role') === 'button' ||
            el.getAttribute('role') === 'link' || el.getAttribute('role') === 'menuitem' ||
            el.getAttribute('role') === 'tab';
          const isInSidebar = rect.left < 300 && rect.top > 50 && rect.top < window.innerHeight;

          // Must be either clickable/button OR in the sidebar area
          if (!isButton && !isClickable && !isInSidebar) continue;

          // Must have short, single-line text (like a nav label)
          // Use textContent of direct text nodes to avoid capturing parent container text
          let text = '';
          if (htmlEl.childElementCount === 0) {
            // Leaf element — use its text directly
            text = htmlEl.innerText?.trim() ?? '';
          } else {
            // Has children — use innerText but skip if too long (means it's a container)
            text = htmlEl.innerText?.trim() ?? '';
          }
          if (!text || text.length > 40 || text.length < 2) continue;
          if (text.includes('\n') && text.split('\n').length > 2) continue;

          // Skip destructive/auth/action buttons
          if (/^(save|cancel|close|delete|submit|ok|yes|no|add|remove|edit|update|create|apply|reset|confirm|send)$/i.test(text)) continue;
          if (/log\s?out|sign\s?out|sign\s?off|logout|signout|sign\s?in|log\s?in|register|upgrade|subscribe|deactivate|export|import|download|upload|share|invite|copy|print/i.test(text)) continue;

          // Skip elements inside forms, tables, dialogs
          if (el.closest('form') || el.closest('table') || el.closest('thead') || el.closest('[role="dialog"]') || el.closest('[role="alertdialog"]')) continue;

          // Skip if a child <a> or <button> was already captured (avoid double-counting)
          if (el.querySelector('a[href]') || (tag !== 'BUTTON' && el.querySelector('button'))) continue;

          const key = `nav:${text}`;
          if (seenHrefs.has(key)) continue;
          seenHrefs.add(key);

          // Build a CSS selector — try multiple strategies
          const elTag = el.tagName.toLowerCase();
          let selector: string;
          const dataTestId = el.getAttribute('data-testid');
          const ariaLabel = el.getAttribute('aria-label');

          if (dataTestId) {
            selector = `[data-testid="${dataTestId}"]`;
          } else if (el.id && !el.id.includes(':')) {
            selector = `#${CSS.escape(el.id)}`;
          } else if (ariaLabel) {
            selector = `${elTag}[aria-label="${CSS.escape(ariaLabel)}"]`;
          } else {
            // Walk up to find a stable anchor (element with id or data-testid)
            let anchor = el.parentElement;
            let depth = 0;
            while (anchor && depth < 5) {
              if (anchor.id && !anchor.id.includes(':')) break;
              if (anchor.getAttribute('data-testid')) break;
              anchor = anchor.parentElement;
              depth++;
            }

            if (anchor && (anchor.id || anchor.getAttribute('data-testid'))) {
              const anchorSel = anchor.id && !anchor.id.includes(':')
                ? `#${CSS.escape(anchor.id)}`
                : `[data-testid="${anchor.getAttribute('data-testid')}"]`;
              const allInAnchor = Array.from(anchor.querySelectorAll(elTag));
              const idx = allInAnchor.indexOf(el);
              selector = idx >= 0 ? `${anchorSel} ${elTag}:nth-of-type(${idx + 1})` : `${anchorSel} ${elTag}`;
            } else {
              // Last resort: use parent > nth-child
              const parent = el.parentElement;
              if (parent) {
                const siblings = Array.from(parent.children);
                const idx = siblings.indexOf(el);
                selector = `${parent.tagName.toLowerCase()} > ${elTag}:nth-child(${idx + 1})`;
              } else {
                continue;
              }
            }
          }

          links.push({ text, href: `click:${selector}` });
        }

        return {
          url: location.pathname,
          fullUrl: location.href,
          title: document.title,
          headings: headings.slice(0, 10),
          formCount: document.querySelectorAll('form, [role="form"]').length,
          tableCount: document.querySelectorAll('table, [role="grid"]').length,
          buttonCount: document.querySelectorAll('button, [role="button"]').length,
          inputCount: document.querySelectorAll('input, select, textarea').length,
          chartCount: document.querySelectorAll('canvas, [class*="chart"], [class*="Chart"]').length,
          links: links.slice(0, 50),
        };
      }

      case 'da:get-state': {
        return this.getBasicState();
      }

      case 'da:inject-token': {
        (window as unknown as Record<string, unknown>).__da_token = payload.token;
        return null;
      }

      case 'da:ping': {
        return null;
      }

      case 'da:init': {
        return { initialized: true };
      }

      default:
        throw new Error(`Unknown command: ${type}`);
    }
  }

  // ─── Post-Action Observation ──────────────────────────────────────────

  /**
   * After a state-changing command (click / type / fill-select / scroll-to /
   * press-key) lands, give the page one frame plus a short settle window so
   * SPA toasts, validation messages, and route changes have rendered, then
   * scan for what's visible. Returned to the parent as part of the command
   * result so the next chat turn's prompt carries POST-ACTION SCREEN STATE.
   *
   * `mayOpenModal` — when true (click / press-key:Enter), wait up to 800ms
   * for an async modal to mount before scanning. Without this, modals that
   * portal-mount with a transition delay (Radix, Headless UI, MUI) would
   * miss the snapshot and the agent would narrate over the wrong screen.
   * Other actions (type, scroll, select) skip the modal wait — feels more
   * synchronous and modals after those actions are rare.
   */
  private async captureObservationAfterAction(mayOpenModal = false): Promise<ScreenObservation | null> {
    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      // Cluster D item 34 — replace fixed 150ms wait with a network-idle gate
      // (200ms quiet, 1.2s ceiling). Catches data-fetch flows where the route
      // has rendered a skeleton but the real content is still in flight; the
      // old fixed wait would snapshot the skeleton and the agent would narrate
      // over a loading state. Falls through to the timeout if the network
      // never quiets (rare — long-poll dashboards) so we still capture
      // something rather than blocking forever.
      await waitForNetworkIdle(200, 1200);
      if (mayOpenModal) {
        // Async path waits up to 800ms for [role=dialog] et al. to mount.
        return await extractScreenObservationAsync(800);
      }
      return extractScreenObservation();
    } catch (err) {
      // Observation is best-effort; never fail the action because of it.
      console.warn('[DemoAgent] extractScreenObservation failed:', err);
      return null;
    }
  }

  // ─── State Capture ────────────────────────────────────────────────────

  private getBasicState(): Record<string, unknown> {
    const visibleElements: string[] = [];
    const selectors = [
      'button:not([disabled])', 'a[href]', 'input', 'select', 'textarea',
      '[role="button"]', '[role="link"]', '[role="tab"]',
      '[data-testid]', '[data-demo-id]',
    ];

    for (const sel of selectors) {
      document.querySelectorAll(sel).forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (!htmlEl.offsetParent && htmlEl.style.position !== 'fixed') return;
        const label = htmlEl.getAttribute('aria-label') || htmlEl.innerText?.trim().slice(0, 40);
        if (label) visibleElements.push(`${el.tagName.toLowerCase()}: ${label}`);
      });
      if (visibleElements.length >= 50) break;
    }

    return {
      url: location.href,
      title: document.title,
      visibleElements: visibleElements.slice(0, 50),
    };
  }

  // ─── Communication ────────────────────────────────────────────────────

  private sendResult(commandId: string, ok: boolean, error?: string, data?: unknown): void {
    this.sendToParent('da:cmd-result', { id: commandId, ok, error, data });
  }

  private sendToParent(type: string, payload: Record<string, unknown>): void {
    const msg: DAMessage = {
      __da: true,
      id: this.generateId(),
      gen: this.currentGen,
      ts: Date.now(),
      dir: 'iframe-to-parent',
      type,
      payload,
    };
    window.parent.postMessage(msg, '*');
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.sendToParent('da:pong', { timestamp: Date.now(), iframeAlive: true });
    }, 5000);
  }

  private observeNavigation(): void {
    // Intercept pushState/replaceState for SPA detection
    const origPushState = history.pushState.bind(history);
    const origReplaceState = history.replaceState.bind(history);

    // C28 — fire on the next animation frame instead of a 100ms debounce.
    // Two pushState calls within 100ms (route + redirect, common in
    // protected routes) used to collapse to one notification, leaving
    // the parent thinking we're on the intermediate URL. requestAnimationFrame
    // gives the SPA router a tick to commit but still surfaces both
    // transitions to the parent — the platform layer dedups by URL on
    // arrival, so duplicate notifications are cheap.
    const notifyNavChange = () => {
      requestAnimationFrame(() => {
        const currentUrl = location.href;
        if (currentUrl !== this.lastUrl) {
          this.lastUrl = currentUrl;
          this.sendToParent('da:nav-change', {
            url: currentUrl,
            title: document.title,
          });
        }
      });
    };

    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      origPushState(...args);
      notifyNavChange();
    };

    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      origReplaceState(...args);
      notifyNavChange();
    };

    window.addEventListener('popstate', notifyNavChange);

    // Poll fallback for edge cases
    this.navPollInterval = setInterval(() => {
      const currentUrl = location.href;
      if (currentUrl !== this.lastUrl) {
        this.lastUrl = currentUrl;
        this.sendToParent('da:nav-change', {
          url: currentUrl,
          title: document.title,
        });
      }
    }, 500);
  }

  private interceptPopups(): void {
    window.open = (url?: string | URL, _target?: string, _features?: string): WindowProxy | null => {
      if (url) {
        const resolvedUrl = new URL(url.toString(), location.href).href;
        location.href = resolvedUrl;
      }
      return null;
    };
  }

  /**
   * Intercept anchor-driven downloads (`<a download>` clicks) so the parent
   * is informed about the file — without this, "Export CSV" buttons just
   * silently trigger a browser download with no signal back to the agent,
   * and the next narration ("here's your file…") is unverifiable. We let
   * the download proceed normally; we just *observe* it. P1 (#66).
   */
  private interceptDownloads(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a[download]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const filename = anchor.getAttribute('download') || anchor.href.split('/').pop() || 'file';
      this.sendToParent('da:download-detected', { filename, href: anchor.href });
    }, { capture: true, passive: true });
  }

  /**
   * Hijack browser-native blocking dialogs (alert / confirm / prompt) so they
   * don't hang the demo. Without this, a customer page calling `confirm("Are
   * you sure?")` from a delete handler freezes the iframe — the agent then
   * times out and the visitor sees a stuck page. We auto-resolve confirm/
   * prompt as accept and surface the dialog text to the parent so the agent
   * can narrate it ("the app asked if I'm sure — yes, deleting now"). PR2.
   */
  private interceptNativeDialogs(): void {
    const post = (kind: 'alert' | 'confirm' | 'prompt', text: string, returned: unknown): void => {
      this.sendToParent('da:dialog-intercepted', { kind, text, returned });
    };
    window.alert = (msg?: unknown): void => {
      post('alert', String(msg ?? ''), undefined);
    };
    window.confirm = (msg?: string): boolean => {
      post('confirm', String(msg ?? ''), true);
      return true;
    };
    window.prompt = (msg?: string, def?: string): string | null => {
      const reply = def ?? '';
      post('prompt', String(msg ?? ''), reply);
      return reply;
    };
  }

  private cleanupHighlights(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
    hideCursor();
  }

  private generateId(): string {
    return `ifa_${++this.idCounter}_${Date.now().toString(36)}`;
  }
}
